import { A, useLocation, useNavigate } from "@solidjs/router";
import { createSignal, For, Show, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";
import {
  allowedApps,
  allowedNav,
  appForPath,
  navGroups,
} from "@/apps/registry";
import { currentUser, signOut } from "@/shared/auth/session";
import { FilterDropdown } from "./FilterDropdown";
import { ChevronDownIcon, MenuIcon, SignOutIcon } from "./icons";
import "./AppShell.css";

export function AppShell(props: { children?: JSX.Element }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = createSignal(false);
  const [switcherOpen, setSwitcherOpen] = createSignal(false);

  const activeApp = () => appForPath(location.pathname);

  const openApp = (path: string) => {
    setSwitcherOpen(false);
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <div id="app">
      <nav id="sidebar" classList={{ open: drawerOpen() }}>
        <div class="sidebar-header">
          <FilterDropdown
            open={switcherOpen()}
            onDismiss={() => setSwitcherOpen(false)}
            class="app-switcher"
            panelClass="app-switcher-panel"
            trigger={
              <button
                type="button"
                class="logo logo-btn"
                onClick={() => setSwitcherOpen(!switcherOpen())}
              >
                <Dynamic component={activeApp().icon} size={28} />
                <span>{activeApp().label}</span>
                <ChevronDownIcon />
              </button>
            }
          >
            {/* Only the apps this user has a page in: an entry that leads
                straight to a redirect is worse than no entry. */}
            <For each={allowedApps()}>
              {(app) => (
                <button
                  type="button"
                  class="app-option"
                  classList={{ active: app.id === activeApp().id }}
                  onClick={() => openApp(allowedNav(app)[0]!.path)}
                >
                  <Dynamic component={app.icon} size={16} />
                  {app.label}
                </button>
              )}
            </For>
          </FilterDropdown>
        </div>

        <ul class="nav-links">
          <For each={navGroups(activeApp())}>
            {(group) => (
              <>
                <Show when={group.heading}>
                  {(heading) => <li class="nav-heading">{heading()}</li>}
                </Show>
                <For each={group.items}>
                  {(item) => (
                    <li>
                      <A
                        class="nav-link"
                        activeClass="active"
                        end={item.end}
                        href={item.path}
                        onClick={() => setDrawerOpen(false)}
                      >
                        <Dynamic component={item.icon} />
                        {item.label}
                      </A>
                    </li>
                  )}
                </For>
                <Show when={group.heading && !group.items.length}>
                  <li class="nav-empty">Coming soon</li>
                </Show>
              </>
            )}
          </For>
        </ul>

        <Show when={currentUser()}>
          {(user) => (
            <div class="sidebar-footer">
              <div class="sidebar-user">
                <div class="sidebar-user-name">
                  {user().firstName} {user().lastName}
                </div>
                <div class="sidebar-user-role">{user().roleTitle || "No role"}</div>
              </div>
              <button
                type="button"
                class="nav-link sidebar-signout"
                onClick={() => {
                  signOut();
                  navigate("/login", { replace: true });
                }}
              >
                <SignOutIcon />
                Sign out
              </button>
            </div>
          )}
        </Show>
      </nav>

      <div
        class="sidebar-overlay"
        classList={{ open: drawerOpen() }}
        onClick={() => setDrawerOpen(false)}
      />

      <main id="content">
        <div class="mobile-topbar">
          <button
            type="button"
            class="mobile-nav-btn"
            aria-label="Open navigation"
            aria-controls="sidebar"
            aria-expanded={drawerOpen()}
            onClick={() => setDrawerOpen(!drawerOpen())}
          >
            <MenuIcon />
          </button>
          <span class="mobile-topbar-title">{activeApp().label}</span>
        </div>
        {props.children}
      </main>
    </div>
  );
}
